import React, { useEffect, useState } from 'react';
import { authAPI, adminAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/common/Button';
import RefundPolicyNotice from '../../components/common/RefundPolicyNotice';
import { clearRefundPolicyCache, DEFAULT_REFUND_POLICY, normalizeRefundPolicy } from '../../utils/refundPolicy';
import toast from 'react-hot-toast';

const AdminSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [savingReward, setSavingReward] = useState(false);
  const [profile, setProfile] = useState({ email: '' });
  const [refundPolicy, setRefundPolicy] = useState(DEFAULT_REFUND_POLICY);
  const [rewardSettings, setRewardSettings] = useState({
    enabled: true,
    ordersPerReward: 10,
    rewardAmount: 500,
  });

  const [security, setSecurity] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileResponse, policyResponse, rewardResponse] = await Promise.all([
          authAPI.getProfile(),
          adminAPI.getRefundPolicySettings(),
          adminAPI.getMediatorRewardSettings(),
        ]);

        const userData = profileResponse.data?.data || profileResponse.data;
        const storedUser = localStorage.getItem('user');
        const storedEmail = storedUser ? (JSON.parse(storedUser)?.email || '') : '';

        setProfile({ email: userData?.email || storedEmail || '' });
        setRefundPolicy(normalizeRefundPolicy(policyResponse.data?.data || policyResponse.data));
        const rewardData = rewardResponse.data?.data || rewardResponse.data || {};
        setRewardSettings({
          enabled: rewardData.enabled !== false,
          ordersPerReward: rewardData.ordersPerReward ?? 10,
          rewardAmount: rewardData.rewardAmount ?? 500,
        });
      } catch (error) {
        const storedUser = localStorage.getItem('user');
        const storedEmail = storedUser ? (JSON.parse(storedUser)?.email || '') : '';
        setProfile({ email: storedEmail });
        toast.error('Failed to load some settings');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSecurityChange = (field, value) => {
    setSecurity((prev) => ({ ...prev, [field]: value }));
  };

  const handlePolicyChange = (field, value) => {
    setRefundPolicy((prev) => ({ ...prev, [field]: value }));
  };

  const handleRewardChange = (field, value) => {
    setRewardSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleRewardSave = async (e) => {
    e.preventDefault();
    setSavingReward(true);
    try {
      const payload = {
        enabled: Boolean(rewardSettings.enabled),
        ordersPerReward: Number(rewardSettings.ordersPerReward),
        rewardAmount: Number(rewardSettings.rewardAmount),
      };
      const response = await adminAPI.updateMediatorRewardSettings(payload);
      const updated = response.data?.data || response.data;
      setRewardSettings({
        enabled: updated.enabled !== false,
        ordersPerReward: updated.ordersPerReward ?? payload.ordersPerReward,
        rewardAmount: updated.rewardAmount ?? payload.rewardAmount,
      });
      toast.success('Mediator reward settings updated');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update reward settings');
    } finally {
      setSavingReward(false);
    }
  };

  const handlePolicySave = async (e) => {
    e.preventDefault();
    setSavingPolicy(true);
    try {
      const payload = {
        reminderDay: Number(refundPolicy.reminderDay),
        firstPenaltyDay: Number(refundPolicy.firstPenaltyDay),
        firstPenaltyAmount: Number(refundPolicy.firstPenaltyAmount),
        secondPenaltyDay: Number(refundPolicy.secondPenaltyDay),
        secondPenaltyAmount: Number(refundPolicy.secondPenaltyAmount),
        formCloseDay: Number(refundPolicy.formCloseDay),
      };

      const response = await adminAPI.updateRefundPolicySettings(payload);
      const updated = normalizeRefundPolicy(response.data?.data || response.data);
      setRefundPolicy(updated);
      clearRefundPolicyCache();
      toast.success('Refund policy updated');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update refund policy');
    } finally {
      setSavingPolicy(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();

    if (!security.currentPassword) {
      toast.error('Current password is required');
      return;
    }
    if (!security.newPassword) {
      toast.error('New password is required');
      return;
    }
    if (security.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    if (security.newPassword !== security.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!profile.email) {
      toast.error('Email not found for this user');
      return;
    }

    setSaving(true);
    try {
      const role = user?.role?.toUpperCase() || 'ADMIN';

      await authAPI.resetPassword(
        profile.email,
        security.newPassword,
        security.confirmPassword,
        role
      );

      toast.success('Password updated successfully');
      setSecurity({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Failed to update password:', error);
      toast.error(error.response?.data?.message || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    background: '#fff',
  };

  return (
    <div style={{ padding: '24px', flex: 1, backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column', width: '100%', overflow: 'auto' }}>
      <div style={{ maxWidth: '860px', margin: '0 auto', width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div style={{ background: '#ffffff', borderRadius: '16px', padding: '32px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
          <h2 className="outfit font-bold text-xl mb-2">Refund Policy Settings</h2>
          <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '20px' }}>
            Configure refund form close days and penalty rules. These values are used across order form, refund form, WhatsApp messages, emails, and penalties.
          </p>

          {loading ? (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading...</p>
          ) : (
            <>
              <RefundPolicyNotice policy={refundPolicy} compact />

              <form onSubmit={handlePolicySave} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginTop: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Reminder starts (day)</label>
                  <input type="number" min="1" value={refundPolicy.reminderDay} onChange={(e) => handlePolicyChange('reminderDay', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>First penalty day</label>
                  <input type="number" min="1" value={refundPolicy.firstPenaltyDay} onChange={(e) => handlePolicyChange('firstPenaltyDay', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>First penalty amount (₹)</label>
                  <input type="number" min="0" step="0.01" value={refundPolicy.firstPenaltyAmount} onChange={(e) => handlePolicyChange('firstPenaltyAmount', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Second penalty day</label>
                  <input type="number" min="1" value={refundPolicy.secondPenaltyDay} onChange={(e) => handlePolicyChange('secondPenaltyDay', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Second penalty amount (₹)</label>
                  <input type="number" min="0" step="0.01" value={refundPolicy.secondPenaltyAmount} onChange={(e) => handlePolicyChange('secondPenaltyAmount', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Form close day</label>
                  <input type="number" min="1" value={refundPolicy.formCloseDay} onChange={(e) => handlePolicyChange('formCloseDay', e.target.value)} style={inputStyle} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Button type="submit" disabled={savingPolicy} loading={savingPolicy}>
                    Save Refund Policy
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>

        <div style={{ background: '#ffffff', borderRadius: '16px', padding: '32px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
          <h2 className="outfit font-bold text-xl mb-2">Mediator Reward Settings</h2>
          <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '20px' }}>
            Configure how mediators earn wallet rewards based on <strong>orders in the current calendar month</strong> (IST).
            Count resets at midnight on the 1st of each month. Rewards are credited automatically at each milestone; pay balances from Mediator Rewards page.
          </p>

          {loading ? (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading...</p>
          ) : (
            <form onSubmit={handleRewardSave} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={rewardSettings.enabled}
                    onChange={(e) => handleRewardChange('enabled', e.target.checked)}
                  />
                  Enable mediator reward program
                </label>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Orders per reward</label>
                <input type="number" min="1" value={rewardSettings.ordersPerReward} onChange={(e) => handleRewardChange('ordersPerReward', e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Reward amount (₹)</label>
                <input type="number" min="0.01" step="0.01" value={rewardSettings.rewardAmount} onChange={(e) => handleRewardChange('rewardAmount', e.target.value)} style={inputStyle} />
              </div>
              <div style={{ gridColumn: '1 / -1', fontSize: '13px', color: '#64748b' }}>
                Example: every {rewardSettings.ordersPerReward || 10} orders <strong>per month</strong> → ₹{rewardSettings.rewardAmount || 0} (resets each month)
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Button type="submit" disabled={savingReward} loading={savingReward}>
                  Save Reward Settings
                </Button>
              </div>
            </form>
          )}
        </div>

        <div style={{ background: '#ffffff', borderRadius: '16px', padding: '32px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
          <h2 className="outfit font-bold text-xl mb-4">Change Password</h2>

          {loading ? (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading...</p>
          ) : (
            <form onSubmit={handlePasswordSave} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Current Password</label>
                <input type="password" value={security.currentPassword} onChange={(e) => handleSecurityChange('currentPassword', e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>New Password</label>
                <input type="password" value={security.newPassword} onChange={(e) => handleSecurityChange('newPassword', e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Confirm Password</label>
                <input type="password" value={security.confirmPassword} onChange={(e) => handleSecurityChange('confirmPassword', e.target.value)} style={inputStyle} />
              </div>
              <Button type="submit" disabled={saving} loading={saving} style={{ width: '100%', justifyContent: 'center' }}>
                Change Password
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
